import { FeaturesQuery } from '@/graphql/graphql';
import { getLinkSessionId } from '@/graphql/authHeader';
import { atom, type SetStateAction } from 'jotai';
import { LayerData } from './layers.atom';

export type SelectedFeature = FeaturesQuery['featuresByLayer'][number] & {
  isMoving?: boolean;
  isEditing?: boolean;
  isDraft?: boolean;
  onSave: (selectedFeature: any) => void;
};

/**
 * Shared links (`?token=`) can never move / edit a *saved* feature: every
 * request carries the link token (see `getAuthToken`), so the save would fail,
 * and the shared page mounts no FeatureMoveActions Save/Cancel bar to leave
 * move mode with. The UI already hides Move / Edit / Delete there (GIS-284);
 * this strips the flags at the atom so no stray path (a creation tool's
 * `selectFeatureForEditing`, an old undo command, a drag handler) can strand
 * the visitor in edit mode. Drafts keep their flags — they are not saved
 * features and own their own OK / Cancel UI.
 */
const stripSharedLinkEditFlags = (feature: SelectedFeature | null): SelectedFeature | null => {
  if (!feature || feature.isDraft) return feature;
  if (!feature.isMoving && !feature.isEditing) return feature;
  if (typeof window === 'undefined' || !getLinkSessionId()) return feature;
  return { ...feature, isMoving: false, isEditing: false };
};

const baseSelectedFeatureAtom = atom<SelectedFeature | null>(null);

export const selectedFeatureAtom = atom(
  (get) => get(baseSelectedFeatureAtom),
  (get, set, update: SetStateAction<SelectedFeature | null>) => {
    const next = typeof update === 'function' ? update(get(baseSelectedFeatureAtom)) : update;
    set(baseSelectedFeatureAtom, stripSharedLinkEditFlags(next));
  },
);

export const layerPropertiesAtom = atom<(Partial<LayerData> & {}) | null>(null);

// Multi-feature highlight, driven by the AttributeTable and rendered by
// MapView + the 3D Viewer. Kept separate from selectedFeatureAtom (which is
// the single edit-target with isMoving/isDraft/onSave semantics).
export const highlightedFeatureIdsAtom = atom<Set<string>>(new Set<string>());
export const highlightedLayerIdAtom = atom<string | null>(null);
